import { CdkMenuModule } from '@angular/cdk/menu';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  OnInit,
  computed,
  effect,
  signal,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PalantirButtonComponent } from '../button/button.component';
import { PalantirContentComponent } from '../content/content.component';
import { PalantirIconComponent } from '../icon/icon.component';
import { PalantirTextComponent } from '../text/text.component';
import { PalantirTooltipModule } from '../tooltip/tooltip.module';
import { PalantirPaginationLayoutEnum } from './pagination.type';
import type { PalantirBasePaginationService, PalantirPaginationLayout } from './pagination.type';

@Component({
  selector: 'palantir-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CdkMenuModule,
    FormsModule,
    PalantirTextComponent,
    PalantirButtonComponent,
    PalantirIconComponent,
    PalantirContentComponent,
    PalantirTooltipModule,
  ],
})
export class PalantirPaginationComponent<
  T extends PalantirBasePaginationService,
> implements OnInit {
  @Input({ required: true }) public service!: T;

  @Input({ required: true }) public firstPageLabel!: string;

  @Input({ required: true }) public lastPageLabel!: string;

  @Input({ required: true }) public nextPageLabel!: string;

  @Input({ required: true }) public previousPageLabel!: string;

  @Input({ required: true }) public perPageLabel!: string;

  @Input() public layout: PalantirPaginationLayout = PalantirPaginationLayoutEnum.default;

  @Output() public readonly paginationChanged = new EventEmitter<void>();

  private isInitialized = false;

  public paginationOptions = signal<number[]>([10, 25, 50, 100, 250]);

  public pageRange = computed<string>(this.computePageRange.bind(this));

  public isFirstPageDisabled = computed(
    // eslint-disable-next-line unicorn/consistent-function-scoping
    () =>
      !this.service.pageInfo()?.hasPreviousPage ||
      this.service.isLoading() ||
      Boolean(this.service.isSearching?.()),
  );

  public isLastPageDisabled = computed(
    // eslint-disable-next-line unicorn/consistent-function-scoping
    () =>
      !this.service.pageInfo()?.hasNextPage ||
      this.service.skip() + this.service.take() >= this.service.totalCount() ||
      this.service.isLoading() ||
      Boolean(this.service.isSearching?.()),
  );

  public constructor() {
    effect(() => {
      this.service.skip();
      this.service.take();

      untracked(() => {
        this.service.search$.next(undefined);
        this.paginationChanged.emit();
      });
    });
  }

  public ngOnInit(): void {
    this.isInitialized = true;
  }

  public async changePaginationSize(size: number): Promise<void> {
    this.service.take.set(size);
    this.service.skip.set(0);
  }

  public async nextPage(): Promise<void> {
    if (this.isLastPageDisabled()) {
      return;
    }

    this.service.skip.update((skip: number) => skip + Number(this.service.take()));
  }

  public async previousPage(): Promise<void> {
    if (this.isFirstPageDisabled()) {
      return;
    }

    this.service.skip.update((skip: number) => Math.max(0, skip - Number(this.service.take())));
  }

  public async firstPage(): Promise<void> {
    if (this.isFirstPageDisabled()) {
      return;
    }

    this.service.skip.set(0);
  }

  public async lastPage(): Promise<void> {
    if (this.isLastPageDisabled()) {
      return;
    }

    const take = this.service.take();
    const totalCount = this.service.totalCount();
    const skip = Math.max(
      0,
      totalCount % take === 0 ? totalCount - take : totalCount - (totalCount % take),
    );
    this.service.skip.set(skip);
  }

  private computePageRange(): string {
    const skip = this.service.skip();
    const take = this.service.take();
    const totalCount = this.service.totalCount();

    if (!totalCount) {
      return this.service.pageRangeBuilder(0, 0, 0);
    }

    const start = skip === 0 ? 1 : Number(skip) + 1;
    const end = Math.min(Number(skip) + Number(take), totalCount);

    return this.service.pageRangeBuilder(start, end, totalCount);
  }

  public trackByFn(index: number): number {
    return index;
  }
}
